/**
 * Consignment Settlement Section for Rental Return Page
 *
 * Displays three sections during rental return:
 * 1. Equipment return (delegated to parent — existing OK/Damaged/Missing flow)
 * 2. Free accessories checklist (INCLUDED items)
 * 3. Consignment settlement (quantity returned vs dispatched, auto-charge calculation)
 *
 * Used inside RentalReturnPage between the equipment inspection step
 * and the payment step.
 */

import type { ConsignmentItem } from '@/api/consignments';
import { Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { cn, formatHuf as formatCurrency } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useMemo } from 'react';

// ============================================
// Types
// ============================================

export interface AccessoryCheckItem {
  id: string;
  name: string;
  quantity: number;
  isReturned: boolean;
}

export interface ConsignmentReturnItem {
  consignmentItemId: string;
  productName: string;
  productSku: string;
  quantityOut: number;
  quantityReturned: number;
  unitPrice: number;
}

export interface ConsignmentSettlementData {
  accessories: AccessoryCheckItem[];
  consignmentItems: ConsignmentReturnItem[];
}

interface ConsignmentSettlementSectionProps {
  /** Free accessories from the package (INCLUDED items) */
  accessories: AccessoryCheckItem[];
  onAccessoryToggle: (id: string) => void;
  /** Consignment items dispatched on delivery note */
  consignmentItems: ConsignmentReturnItem[];
  onConsignmentQuantityChange: (itemId: string, quantityReturned: number) => void;
  /** Rental fee totals (from parent) */
  rentalFeeTotal: number;
  /** Deposit paid (from parent) */
  depositPaid: number;
  /** Late fee (from parent) */
  lateFee: number;
  /** Damage charges (from parent) */
  damageCharges: number;
}

// ============================================
// Settlement money logic (exported pure helper — teszt-coverage regression-guard)
// ============================================

/**
 * Kaució-elszámolás money-logika: consignment-charges (ki-nem-hozott mennyiség × egységár),
 * grand-total (bérleti díj + consignment), és a kaució-refund (a levonások után, 0-floor).
 * Pure → unit-tesztelhető render-setup nélkül. A komponens useMemo-zza + render-eli.
 */
export function computeConsignmentSettlement(input: {
  rentalFeeTotal: number;
  consignmentItems: ConsignmentReturnItem[];
  depositPaid: number;
  lateFee: number;
  damageCharges: number;
}): {
  consignmentCharges: number;
  grandTotal: number;
  totalDeductions: number;
  depositRefund: number;
} {
  const consignmentCharges = input.consignmentItems.reduce((sum, item) => {
    const charged = Math.max(0, item.quantityOut - item.quantityReturned);
    return sum + charged * item.unitPrice;
  }, 0);
  const grandTotal = input.rentalFeeTotal + consignmentCharges;
  const totalDeductions = input.lateFee + input.damageCharges + consignmentCharges;
  const depositRefund = Math.max(0, input.depositPaid - totalDeductions);
  return { consignmentCharges, grandTotal, totalDeductions, depositRefund };
}

// ============================================
// Component
// ============================================

export function ConsignmentSettlementSection({
  accessories,
  onAccessoryToggle,
  consignmentItems,
  onConsignmentQuantityChange,
  rentalFeeTotal,
  depositPaid,
  lateFee,
  damageCharges,
}: ConsignmentSettlementSectionProps) {
  const { t } = useTranslation();

  // A kaució-elszámolás money-logika a computeConsignmentSettlement pure helperben
  // (teszt-coverage + DRY); a komponens csak useMemo-zza + render-eli. A totalDeductions
  // a helper-belső (a depositRefund-hoz) — itt nem render-elt, ezért nem destrukturáljuk.
  const { consignmentCharges, grandTotal, depositRefund } = useMemo(
    () =>
      computeConsignmentSettlement({
        rentalFeeTotal,
        consignmentItems,
        depositPaid,
        lateFee,
        damageCharges,
      }),
    [rentalFeeTotal, consignmentItems, depositPaid, lateFee, damageCharges]
  );

  // Check for over-return warnings
  const hasOverReturn = consignmentItems.some(item => item.quantityReturned > item.quantityOut);

  return (
    <div className="space-y-6">
      {/* Section 2: Free Accessories Checklist */}
      {accessories.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                2
              </span>
              {t('rental.consignment.freeAccessories')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('rental.consignment.freeAccessoriesDesc')}
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {accessories.map(acc => (
                <label
                  key={acc.id}
                  className={cn(
                    'flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors',
                    acc.isReturned
                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/10'
                      : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/10'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={acc.isReturned}
                      onChange={() => onAccessoryToggle(acc.id)}
                      className="h-4 w-4 rounded border-muted text-primary"
                    />
                    <span className="text-sm font-medium text-foreground">{acc.name}</span>
                    {acc.quantity > 1 && (
                      <span className="text-xs text-muted-foreground">&times; {acc.quantity}</span>
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      acc.isReturned
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    )}
                  >
                    {acc.isReturned
                      ? t('rental.consignment.accessoryReturned')
                      : t('rental.consignment.accessoryMissing')}
                  </span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 3: Consignment Settlement */}
      {consignmentItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-xs text-white">
                3
              </span>
              {t('rental.consignment.settlement')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Warning for over-return */}
            {hasOverReturn && (
              <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  {t('rental.consignment.warningOverReturn')}
                </p>
              </div>
            )}

            {/* Consignment items table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-muted text-muted-foreground">
                    <th className="pb-2 text-left font-medium">
                      {t('rental.consignment.itemName')}
                    </th>
                    <th className="pb-2 text-center font-medium">
                      {t('rental.consignment.dispatched')}
                    </th>
                    <th className="pb-2 text-center font-medium">
                      {t('rental.consignment.returned')}
                    </th>
                    <th className="pb-2 text-center font-medium">
                      {t('rental.consignment.charged')}
                    </th>
                    <th className="pb-2 text-right font-medium">
                      {t('rental.consignment.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {consignmentItems.map(item => {
                    const charged = Math.max(0, item.quantityOut - item.quantityReturned);
                    const chargedAmount = charged * item.unitPrice;
                    const isOverReturn = item.quantityReturned > item.quantityOut;

                    return (
                      <tr
                        key={item.consignmentItemId}
                        className={cn(
                          'border-b border-muted/50',
                          isOverReturn && 'bg-red-50 dark:bg-red-900/10'
                        )}
                      >
                        <td className="py-3">
                          <div>
                            <p className="font-medium text-foreground">{item.productName}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.productSku} &mdash; {formatCurrency(item.unitPrice)}/db
                            </p>
                          </div>
                        </td>
                        <td className="py-3 text-center text-foreground">{item.quantityOut}</td>
                        <td className="py-3">
                          <div className="flex items-center justify-center">
                            <Input
                              type="number"
                              min={0}
                              max={item.quantityOut}
                              value={item.quantityReturned}
                              onChange={e =>
                                onConsignmentQuantityChange(
                                  item.consignmentItemId,
                                  Math.max(0, parseInt(e.target.value, 10) || 0)
                                )
                              }
                              className={cn(
                                'h-8 w-20 text-center',
                                isOverReturn && 'border-red-500 dark:border-red-500'
                              )}
                            />
                          </div>
                        </td>
                        <td className="py-3 text-center">
                          <span
                            className={cn(
                              'font-semibold',
                              charged > 0
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-green-600 dark:text-green-400'
                            )}
                          >
                            {charged}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <span
                            className={cn(
                              'font-medium',
                              chargedAmount > 0 ? 'text-foreground' : 'text-muted-foreground'
                            )}
                          >
                            {formatCurrency(chargedAmount)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settlement Summary */}
      {consignmentItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('rental.consignment.settlementSummary')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {/* Rental fee */}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('rental.consignment.rentalFeeTotal')}
                </span>
                <span className="text-foreground">{formatCurrency(rentalFeeTotal)}</span>
              </div>

              {/* Consignment charges */}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('rental.consignment.consignmentCharges')}
                </span>
                <span className="font-medium text-amber-600 dark:text-amber-400">
                  {formatCurrency(consignmentCharges)}
                </span>
              </div>

              <hr className="border-muted" />

              {/* Grand total */}
              <div className="flex justify-between text-sm font-bold">
                <span className="text-foreground">{t('rental.consignment.grandTotal')}</span>
                <span className="text-foreground">{formatCurrency(grandTotal)}</span>
              </div>

              {/* Late fee */}
              {lateFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-red-600 dark:text-red-400">{t('rental.lateFeeLabel')}</span>
                  <span className="text-red-600 dark:text-red-400">-{formatCurrency(lateFee)}</span>
                </div>
              )}

              {/* Damage charges */}
              {damageCharges > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-red-600 dark:text-red-400">
                    {t('rental.damageChargeLabel')}
                  </span>
                  <span className="text-red-600 dark:text-red-400">
                    -{formatCurrency(damageCharges)}
                  </span>
                </div>
              )}

              <hr className="border-muted" />

              {/* Deposit refund */}
              <div className="flex justify-between">
                <span className="font-semibold text-foreground">
                  {t('rental.consignment.depositRefund')}
                </span>
                <span
                  className={cn(
                    'text-lg font-bold',
                    depositRefund > 0 ? 'text-green-600 dark:text-green-400' : 'text-foreground'
                  )}
                >
                  {formatCurrency(depositRefund)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================
// Helper: Build ConsignmentReturnItem[] from API data
// ============================================

export function buildConsignmentReturnItems(items: ConsignmentItem[]): ConsignmentReturnItem[] {
  return items.map(item => ({
    consignmentItemId: item.id,
    productName: item.productName,
    productSku: item.productSku,
    quantityOut: item.quantityOut,
    quantityReturned: item.quantityOut, // Default: return all
    unitPrice: item.unitPrice,
  }));
}
